import type { IReportingRepository, IRepositoryFactory, ISettingsRepository } from '@app/domain/ports';
import type {
  IAccountRepository,
  IAccountTransferRepository,
  IBackupRepository,
  IBankRequestCacheRepository,
  ICategoryRepository,
  ICreditorRepository,
  ICreditorPaymentRepository,
  IDebtorRepository,
  IDebtorPaymentRepository,
  ICurrencyRepository,
  ICurrencyExchangeRepository,
  IExpenseRepository,
  IExternalTransactionRepository,
  IIncomeRepository,
  IMetadataKeyRepository,
  ICurrencyRateRepository,
  IPlannedExpenseRepository,
  IPlannedIncomeRepository,
  IReportRepository,
  ITagRepository,
  ITransactionManager,
  IChangeTracker,
} from '@app/domain/ports';
import { SqliteConfigurationError } from '../../errors';

import { ChangeTracker, RoutingMetadataKeyRepository, wrapProviderWithChangeTracker } from '../repositories';
import { SqliteProvider } from '../providers/sqlite.provider';
import { ProxySqliteProvider } from '../providers/proxy-sqlite.provider';
import { IndexedDbFactory } from './indexeddb-provider.factory';
import { SqliteRepositoryFactory } from './sqlite-repository.factory';
import { IndexedDbRepositoryFactory } from './indexeddb-repository.factory';
import { LocalStorageRepositoryFactory } from './local-storage-repository.factory';

/**
 * Composite repository factory that delegates to specialized factories.
 * Provides a unified interface while maintaining separation between SQLite, IndexedDB, and localStorage concerns.
 */
export class DefaultRepositoryFactory implements IRepositoryFactory {
  private readonly changeTracker: ChangeTracker;
  private readonly indexedDbFactory: IndexedDbRepositoryFactory;
  private readonly localStorageFactory: LocalStorageRepositoryFactory;
  private readonly proxyProvider: ProxySqliteProvider;
  private readonly sqliteFactory: SqliteRepositoryFactory;

  constructor(options: { bankRequestCacheTtlMs: number }) {
    this.changeTracker = new ChangeTracker();
    this.indexedDbFactory = new IndexedDbRepositoryFactory(new IndexedDbFactory(), options);
    this.localStorageFactory = new LocalStorageRepositoryFactory();
    // Create a single stable proxy that all repository singletons reference.
    // When the underlying SQLite provider is replaced (e.g. after idle-lock
    // re-login), only the proxy delegate is swapped – existing DI singletons
    // automatically route to the new provider without being recreated.
    this.proxyProvider = new ProxySqliteProvider();
    const trackedProxy = wrapProviderWithChangeTracker(this.proxyProvider, this.changeTracker);
    this.sqliteFactory = new SqliteRepositoryFactory(trackedProxy);
  }

  /**
   * Sets (or replaces) the SQLite provider.
   * Updates the proxy delegate so all existing repository singletons immediately
   * start routing calls to the new provider.
   */
  setSqliteProvider(provider: SqliteProvider): void {
    this.proxyProvider.setDelegate(provider);
  }

  getChangeTracker(): IChangeTracker {
    return this.changeTracker;
  }

  private ensureSqliteFactory(): SqliteRepositoryFactory {
    if (!this.proxyProvider.hasDelegate()) {
      throw new SqliteConfigurationError(
        'SqliteProvider has not been initialized. Call setSqliteProvider() before creating SQLite-dependent repositories.',
      );
    }

    return this.sqliteFactory;
  }

  createAccountRepository(): IAccountRepository {
    return this.ensureSqliteFactory().createAccountRepository();
  }

  createAccountTransferRepository(): IAccountTransferRepository {
    return this.ensureSqliteFactory().createAccountTransferRepository();
  }

  createBackupRepository(): IBackupRepository {
    return this.indexedDbFactory.createBackupRepository();
  }

  createBankRequestCacheRepository(): IBankRequestCacheRepository {
    return this.indexedDbFactory.createBankRequestCacheRepository();
  }

  createCategoryRepository(): ICategoryRepository {
    return this.ensureSqliteFactory().createCategoryRepository();
  }

  createCreditorRepository(): ICreditorRepository {
    return this.ensureSqliteFactory().createCreditorRepository();
  }

  createCreditorPaymentRepository(): ICreditorPaymentRepository {
    return this.ensureSqliteFactory().createCreditorPaymentRepository();
  }

  createDebtorRepository(): IDebtorRepository {
    return this.ensureSqliteFactory().createDebtorRepository();
  }

  createDebtorPaymentRepository(): IDebtorPaymentRepository {
    return this.ensureSqliteFactory().createDebtorPaymentRepository();
  }

  createCurrencyRepository(): ICurrencyRepository {
    return this.ensureSqliteFactory().createCurrencyRepository();
  }

  createCurrencyExchangeRepository(): ICurrencyExchangeRepository {
    return this.ensureSqliteFactory().createCurrencyExchangeRepository();
  }

  createExpenseRepository(): IExpenseRepository {
    return this.ensureSqliteFactory().createExpenseRepository();
  }

  createPlannedExpenseRepository(): IPlannedExpenseRepository {
    return this.ensureSqliteFactory().createPlannedExpenseRepository();
  }

  createPlannedIncomeRepository(): IPlannedIncomeRepository {
    return this.ensureSqliteFactory().createPlannedIncomeRepository();
  }

  createTransactionManager(): ITransactionManager {
    return this.ensureSqliteFactory().createTransactionManager();
  }

  createExternalTransactionRepository(): IExternalTransactionRepository {
    return this.ensureSqliteFactory().createExternalTransactionRepository();
  }

  createIncomeRepository(): IIncomeRepository {
    return this.ensureSqliteFactory().createIncomeRepository();
  }

  createMetadataKeyRepository(): IMetadataKeyRepository {
    const primaryRepository = this.indexedDbFactory.createMetadataKeyRepository();
    const secondaryRepository = this.localStorageFactory.createMetadataKeyRepository();
    return new RoutingMetadataKeyRepository(primaryRepository, secondaryRepository);
  }

  createCurrencyRateRepository(): ICurrencyRateRepository {
    return this.ensureSqliteFactory().createCurrencyRateRepository();
  }

  createReportRepository(): IReportRepository {
    return this.indexedDbFactory.createReportRepository();
  }

  createReportingRepository(): IReportingRepository {
    return this.ensureSqliteFactory().createReportingRepository();
  }

  createSettingsRepository(): ISettingsRepository {
    return this.localStorageFactory.createSettingsRepository();
  }

  createTagRepository(): ITagRepository {
    return this.ensureSqliteFactory().createTagRepository();
  }
}
